import { ApolloClient, ApolloProvider, gql, NormalizedCacheObject } from '@apollo/client'
import * as React from 'react'
import { FC, useEffect, useState } from 'react'

import { RegistrationProps } from 'auth/types'
import apolloClient from 'clients/apollo'
import { parseURLParams } from 'shared/helpers'
import { RegistrationForm } from './form'

const SIGNUP = gql`
  mutation signup($user: NewUser!) {
    signup(user: $user) {
      account {
        id
        name
      }
      user {
        id
        name
        email
        roles
      }
      errors
    }
  }
`

const ACCEPT_INVITE = gql`
  mutation acceptInvite($invitationToken: String!, $accountId: ID!, $name: String!, $password: String!) {
    acceptInvite(invitationToken: $invitationToken, accountId: $accountId, name: $name, password: $password) {
      account {
        id
        name
      }
      user {
        id
        name
        email
        roles
      }
      errors
    }
  }
`

const RegistrationView: FC<RegistrationProps> = ({ onSignupSuccess }) => {
  const [acceptInviteArgs, setAcceptInviteArgs] = useState<{ [key: string]: string }>(null)
  const [client, setClient] = useState<ApolloClient<NormalizedCacheObject>>(null)
  const [mutation, setMutation] = useState<any>(SIGNUP)

  const generateSubmitPayload = (values: any): { [key: string]: string } => {
    if (acceptingInvite()) return Object.assign(acceptInviteArgs, values)
    else {
      // native signup case
      return { user: values }
    }
  }

  const acceptingInvite = (): boolean => acceptInviteArgs != null

  const setUp = () => {
    const acceptInviteArgs = parseURLParams([
      { key: 'account_id', parsedKey: 'accountId' },
      { key: 'invitation_token', parsedKey: 'invitationToken' },
    ])

    if (Object.keys(acceptInviteArgs).length == 2) {
      setAcceptInviteArgs(acceptInviteArgs)
      setMutation(ACCEPT_INVITE)
    }
  }

  const onSuccess = (successPayload: any, handleErrors: (errors: any) => void): void => {
    const { errors, account, user } = acceptingInvite() ? successPayload.acceptInvite : successPayload.signup
    if (errors.length > 0) handleErrors(errors)
    else onSignupSuccess({ account, user }, !acceptingInvite())
  }

  useEffect(() => {
    setClient(apolloClient)
    setUp()
  }, [])

  if (!client) return null

  return (
    <ApolloProvider client={client}>
      <RegistrationForm
        generateSubmitPayload={generateSubmitPayload}
        hideEmail={acceptingInvite()}
        mutation={mutation}
        onSuccess={onSuccess}
      />
    </ApolloProvider>
  )
}

export default RegistrationView
